import { create } from 'zustand';
import type { StoreApi, UseBoundStore } from 'zustand';
import { FacetDTO } from '@/scripts/domain/Facets';
import { WizardProductSearchResult } from '@/scripts/domain/WizardProductSearch';
import { SearchType } from '@/scripts/domain/shared';

// Strategy interface for handling different search types
interface SearchTypeStrategy {
  getSearchValue(state: GlobalState): string;
  setSearchValue(
    set: (partial: Partial<GlobalState>) => void,
    get: () => GlobalState,
    value: string
  ): void;
  getPageNumber(state: GlobalState): number;
  setPageNumber(
    set: (partial: Partial<GlobalState>) => void,
    get: () => GlobalState,
    pageNumber: number
  ): void;
  getFacets(state: GlobalState): FacetDTO[];
  setFacets(
    set: (partial: Partial<GlobalState>) => void,
    get: () => GlobalState,
    facets: FacetDTO[]
  ): void;
}

// Concrete strategy for blog posts
class BlogPostStrategy implements SearchTypeStrategy {
  getSearchValue(state: GlobalState): string {
    return state.blogSearchValue;
  }

  setSearchValue(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    value: string
  ): void {
    set({ blogSearchValue: value, blogPageNumber: 1 });
  }

  getPageNumber(state: GlobalState): number {
    return state.blogPageNumber;
  }

  setPageNumber(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    pageNumber: number
  ): void {
    set({ blogPageNumber: pageNumber });
  }

  getFacets(state: GlobalState): FacetDTO[] {
    return state.blogFacets;
  }

  setFacets(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    facets: FacetDTO[]
  ): void {
    set({ blogFacets: facets, blogPageNumber: 1 });
  }
}

// Concrete strategy for news
class NewsStrategy implements SearchTypeStrategy {
  getSearchValue(state: GlobalState): string {
    return state.newsSearchValue;
  }

  setSearchValue(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    value: string
  ): void {
    set({ newsSearchValue: value, newsPageNumber: 1 });
  }

  getPageNumber(state: GlobalState): number {
    return state.newsPageNumber;
  }

  setPageNumber(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    pageNumber: number
  ): void {
    set({ newsPageNumber: pageNumber });
  }

  getFacets(state: GlobalState): FacetDTO[] {
    return state.newsFacets;
  }

  setFacets(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    facets: FacetDTO[]
  ): void {
    set({ newsFacets: facets, newsPageNumber: 1 });
  }
}

// Concrete strategy for customer stories
class CustomerStoriesStrategy implements SearchTypeStrategy {
  getSearchValue(state: GlobalState): string {
    return state.customerStoriesSearchValue;
  }

  setSearchValue(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    value: string
  ): void {
    set({ customerStoriesSearchValue: value, customerStoriesPageNumber: 1 });
  }

  getPageNumber(state: GlobalState): number {
    return state.customerStoriesPageNumber;
  }

  setPageNumber(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    pageNumber: number
  ): void {
    set({ customerStoriesPageNumber: pageNumber });
  }

  getFacets(state: GlobalState): FacetDTO[] {
    return state.customerStoriesFacets;
  }

  setFacets(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    facets: FacetDTO[]
  ): void {
    set({ customerStoriesFacets: facets, customerStoriesPageNumber: 1 });
  }
}

// Concrete strategy for products
class ProductStrategy implements SearchTypeStrategy {
  getSearchValue(state: GlobalState): string {
    return state.productSearchValue;
  }

  setSearchValue(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    value: string
  ): void {
    set({ productSearchValue: value, productPageNumber: 1 });
  }

  getPageNumber(state: GlobalState): number {
    return state.productPageNumber;
  }

  setPageNumber(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    pageNumber: number
  ): void {
    set({ productPageNumber: pageNumber });
  }

  getFacets(state: GlobalState): FacetDTO[] {
    return state.productFacets;
  }

  setFacets(
    set: (partial: Partial<GlobalState>) => void,
    _get: () => GlobalState,
    facets: FacetDTO[]
  ): void {
    set({ productFacets: facets, productPageNumber: 1 });
  }
}

class SearchListingStrategy implements SearchTypeStrategy {
  getSearchValue(state: GlobalState): string {
    return state.searchListing.searchValue;
  }

  setSearchValue(
    set: (partial: Partial<GlobalState>) => void,
    get: () => GlobalState,
    value: string
  ): void {
    set({
      searchListing: {
        searchValue: value,
        facets: get().searchListing.facets,
        pageNumber: 1,
        totalResults: get().searchListing.totalResults,
        searchListingTotal: get().searchListing.searchListingTotal,
        productsSectionTotal: get().searchListing.productsSectionTotal,
        productsByBrandTotal: get().searchListing.productsByBrandTotal
      }
    });
  }

  getPageNumber(state: GlobalState): number {
    return state.searchListing.pageNumber;
  }

  setPageNumber(
    set: (partial: Partial<GlobalState>) => void,
    get: () => GlobalState,
    pageNumber: number
  ): void {
    set({
      searchListing: {
        searchValue: get().searchListing.searchValue,
        facets: get().searchListing.facets,
        pageNumber,
        totalResults: get().searchListing.totalResults,
        searchListingTotal: get().searchListing.searchListingTotal,
        productsSectionTotal: get().searchListing.productsSectionTotal,
        productsByBrandTotal: get().searchListing.productsByBrandTotal
      }
    });
  }

  getFacets(state: GlobalState): FacetDTO[] {
    return state.searchListing.facets;
  }

  setFacets(
    set: (partial: Partial<GlobalState>) => void,
    get: () => GlobalState,
    facets: FacetDTO[]
  ): void {
    set({
      searchListing: {
        searchValue: get().searchListing.searchValue,
        facets,
        pageNumber: 1,
        totalResults: get().searchListing.totalResults,
        searchListingTotal: get().searchListing.searchListingTotal,
        productsSectionTotal: get().searchListing.productsSectionTotal,
        productsByBrandTotal: get().searchListing.productsByBrandTotal
      }
    });
  }
}

// Factory for creating the appropriate strategy
class SearchTypeStrategyFactory {
  private static readonly strategies: Record<SearchType, SearchTypeStrategy> = {
    'blog-post': new BlogPostStrategy(),
    news: new NewsStrategy(),
    'customer-stories': new CustomerStoriesStrategy(),
    product: new ProductStrategy(),
    'search-listing': new SearchListingStrategy()
  };

  static getStrategy(type: SearchType): SearchTypeStrategy {
    if (!type) {
      throw new Error('Search type is required but was empty or undefined');
    }
    const strategy = this.strategies[type];
    if (!strategy) {
      throw new Error(`Unsupported search type: "${type}"`);
    }
    return strategy;
  }
}

interface GlobalState {
  mockInputValue: string;
  // Search values for different types
  searchValue: string;
  blogSearchValue: string;
  newsSearchValue: string;
  productSearchValue: string;
  customerStoriesSearchValue: string;
  // Search listing specific state
  searchListing: {
    searchValue: string;
    facets: FacetDTO[];
    pageNumber: number;
    totalResults: number;
    searchListingTotal: number;
    productsSectionTotal: number;
    productsByBrandTotal: number;
  };
  // Setters for search values
  setMockInputValue: (mockInputValue: string) => void;
  setSearchValue: (searchValue: string) => void;
  setBlogSearchValue: (searchValue: string) => void;
  setNewsSearchValue: (searchValue: string) => void;
  setProductSearchValue: (searchValue: string) => void;
  setCustomerStoriesSearchValue: (searchValue: string) => void;
  // Search listing setters
  setSearchListingSearchValue: (searchValue: string) => void;
  setSearchListingFacets: (facets: FacetDTO[]) => void;
  setSearchListingPageNumber: (pageNumber: number) => void;
  setSearchListingTotalResults: (totalResults: number) => void;
  setSearchListingSearchTotal: (searchListingTotal: number) => void;
  setSearchListingProductsSectionTotal: (productsSectionTotal: number) => void;
  setSearchListingProductsByBrandTotal: (productsByBrandTotal: number) => void;
  // Facets for different types
  blogFacets: FacetDTO[];
  newsFacets: FacetDTO[];
  productFacets: FacetDTO[];
  customerStoriesFacets: FacetDTO[];
  setBlogFacets: (facets: FacetDTO[]) => void;
  setNewsFacets: (facets: FacetDTO[]) => void;
  setProductFacets: (facets: FacetDTO[]) => void;
  setCustomerStoriesFacets: (facets: FacetDTO[]) => void;
  // Pagination for different types
  pageNumber: number;
  blogPageNumber: number;
  newsPageNumber: number;
  productPageNumber: number;
  customerStoriesPageNumber: number;
  setPageNumber: (pageNumber: number) => void;
  setBlogPageNumber: (pageNumber: number) => void;
  setNewsPageNumber: (pageNumber: number) => void;
  setProductPageNumber: (pageNumber: number) => void;
  setCustomerStoriesPageNumber: (pageNumber: number) => void;
  // Helper methods to get/set values by type
  getSearchValueByType: (type: SearchType) => string;
  setSearchValueByType: (type: SearchType, searchValue: string) => void;
  getPageNumberByType: (type: SearchType) => number;
  setPageNumberByType: (type: SearchType, pageNumber: number) => void;
  getFacetsByType: (type: SearchType) => FacetDTO[];
  setFacetsByType: (type: SearchType, facets: FacetDTO[]) => void;
  // Product-specific helper methods
  getProductSearchValue: () => string;
  getProductFacets: () => FacetDTO[];
  getProductPageNumber: () => number;
  predictiveSearchValue: string;
  setPredictiveSearchValue: (value: string) => void;
  searchParameter: string;
  setSearchParameter: (value: string) => void;
  // Wizard Quiz
  wizardCurrentStep: number;
  setWizardCurrentStep: (step: number) => void;
  wizardSelectedOptions: Record<number, string[]>;
  setWizardSelectedOptions: (step: number, options: string[]) => void;
  // Wizard Product Search
  wizardProductSearchResult: WizardProductSearchResult | null;
  setWizardProductSearchResult: (result: WizardProductSearchResult | null) => void;
  wizardShouldRedirect: boolean;
  setWizardShouldRedirect: (shouldRedirect: boolean) => void;
  productRangeSlider: [number, number];
  setProductRangeSlider: (value: [number, number]) => void;
  productSingleSlider: { value: number; min: number; max: number };
  setProductSingleSlider: (value: number) => void;
  setProductSingleSliderRange: (min: number, max: number) => void;
  getPredictiveSearchValue: () => string;
  // Search results
  totalSearchResults: number;
  setTotalSearchResults: (total: number) => void;
}

// Ensure a single store instance across code-split bundles (CMS integration)
type GlobalStore = UseBoundStore<StoreApi<GlobalState>>;

const existingStore = (typeof window !== 'undefined' &&
  (window as unknown as { __logisnextGlobalStore?: unknown }).__logisnextGlobalStore) as
  | GlobalStore
  | undefined;

const internalStore: GlobalStore =
  existingStore ??
  create<GlobalState>((set, get) => ({
    mockInputValue: '',
    searchValue: '',
    blogSearchValue: '',
    newsSearchValue: '',
    productSearchValue: '',
    customerStoriesSearchValue: '',
    searchListing: {
      searchValue: '',
      facets: [],
      pageNumber: 1,
      totalResults: 0,
      searchListingTotal: 0,
      productsSectionTotal: 0,
      productsByBrandTotal: 0
    },
    setMockInputValue: (mockInputValue: string) => set({ mockInputValue }),
    setSearchValue: (searchValue: string) => set({ searchValue, pageNumber: 1 }),
    setBlogSearchValue: (value: string) => set({ blogSearchValue: value, blogPageNumber: 1 }),
    setNewsSearchValue: (value: string) => set({ newsSearchValue: value, newsPageNumber: 1 }),
    setProductSearchValue: (value: string) =>
      set({ productSearchValue: value, productPageNumber: 1 }),
    setCustomerStoriesSearchValue: (value: string) =>
      set({ customerStoriesSearchValue: value, customerStoriesPageNumber: 1 }),
    setSearchListingSearchValue: (searchValue: string) =>
      set(state => ({
        searchListing: {
          ...state.searchListing,
          searchValue,
          pageNumber: 1
        }
      })),
    setSearchListingFacets: (facets: FacetDTO[]) =>
      set(state => ({
        searchListing: {
          ...state.searchListing,
          facets,
          pageNumber: 1
        }
      })),
    setSearchListingPageNumber: (pageNumber: number) =>
      set(state => ({
        searchListing: {
          ...state.searchListing,
          pageNumber
        }
      })),
    setSearchListingTotalResults: (totalResults: number) =>
      set(state => ({
        searchListing: {
          ...state.searchListing,
          totalResults
        }
      })),
    setSearchListingSearchTotal: (searchListingTotal: number) =>
      set(state => ({
        searchListing: {
          ...state.searchListing,
          searchListingTotal
        }
      })),
    setSearchListingProductsSectionTotal: (productsSectionTotal: number) =>
      set(state => ({
        searchListing: {
          ...state.searchListing,
          productsSectionTotal
        }
      })),
    setSearchListingProductsByBrandTotal: (productsByBrandTotal: number) =>
      set(state => ({
        searchListing: {
          ...state.searchListing,
          productsByBrandTotal
        }
      })),
    blogFacets: [],
    newsFacets: [],
    productFacets: [],
    customerStoriesFacets: [],
    setBlogFacets: (facets: FacetDTO[]) => set({ blogFacets: facets, blogPageNumber: 1 }),
    setNewsFacets: (facets: FacetDTO[]) => set({ newsFacets: facets, newsPageNumber: 1 }),
    setProductFacets: (facets: FacetDTO[]) => set({ productFacets: facets, productPageNumber: 1 }),
    setCustomerStoriesFacets: (facets: FacetDTO[]) =>
      set({ customerStoriesFacets: facets, customerStoriesPageNumber: 1 }),
    pageNumber: 1,
    blogPageNumber: 1,
    newsPageNumber: 1,
    productPageNumber: 1,
    customerStoriesPageNumber: 1,
    setPageNumber: (pageNumber: number) => set({ pageNumber }),
    setBlogPageNumber: (page: number) => set({ blogPageNumber: page }),
    setNewsPageNumber: (page: number) => set({ newsPageNumber: page }),
    setProductPageNumber: (page: number) => set({ productPageNumber: page }),
    setCustomerStoriesPageNumber: (page: number) => set({ customerStoriesPageNumber: page }),
    // Helper methods using strategy pattern
    getSearchValueByType: (type: SearchType) => {
      const state = get();
      const strategy = SearchTypeStrategyFactory.getStrategy(type);
      return strategy.getSearchValue(state);
    },
    setSearchValueByType: (type: SearchType, searchValue: string) => {
      const strategy = SearchTypeStrategyFactory.getStrategy(type);
      strategy.setSearchValue(set, get, searchValue);
    },
    getPageNumberByType: (type: SearchType) => {
      const state = get();
      const strategy = SearchTypeStrategyFactory.getStrategy(type);
      return strategy.getPageNumber(state);
    },
    setPageNumberByType: (type: SearchType, pageNumber: number) => {
      const strategy = SearchTypeStrategyFactory.getStrategy(type);
      strategy.setPageNumber(set, get, pageNumber);
    },
    getFacetsByType: (type: SearchType) => {
      const state = get();
      const strategy = SearchTypeStrategyFactory.getStrategy(type);
      return strategy.getFacets(state);
    },
    setFacetsByType: (type: SearchType, facets: FacetDTO[]) => {
      const strategy = SearchTypeStrategyFactory.getStrategy(type);
      strategy.setFacets(set, get, facets);
    },
    // Product-specific helper methods
    getProductSearchValue: () => {
      return get().productSearchValue;
    },
    getProductFacets: () => {
      return get().productFacets;
    },
    getProductPageNumber: () => {
      return get().productPageNumber;
    },
    predictiveSearchValue: '',
    setPredictiveSearchValue: (value: string) => set({ predictiveSearchValue: value }),
    searchParameter: 'q',
    setSearchParameter: (value: string) => set({ searchParameter: value }),
    getPredictiveSearchValue: () => {
      return get().predictiveSearchValue;
    },
    // Wizard Quiz
    wizardCurrentStep: 0,
    setWizardCurrentStep: (step: number) => set({ wizardCurrentStep: step }),
    wizardSelectedOptions: {},
    setWizardSelectedOptions: (step: number, options: string[]) => {
      set({
        wizardSelectedOptions: {
          ...get().wizardSelectedOptions,
          [step]: options
        }
      });
    },
    // Wizard Product Search
    wizardProductSearchResult: null,
    setWizardProductSearchResult: (result: WizardProductSearchResult | null) =>
      set({ wizardProductSearchResult: result }),
    wizardShouldRedirect: false,
    setWizardShouldRedirect: (shouldRedirect: boolean) =>
      set({ wizardShouldRedirect: shouldRedirect }),
    productRangeSlider: [0, 20000], // traer del data attribute
    setProductRangeSlider: (value: [number, number]) => set({ productRangeSlider: value }),
    productSingleSlider: { value: 1000, min: 0, max: 1000 }, // traer del data attribute
    setProductSingleSlider: (value: number) =>
      set({ productSingleSlider: { ...get().productSingleSlider, value } }),
    setProductSingleSliderRange: (min: number, max: number) =>
      set({ productSingleSlider: { ...get().productSingleSlider, min, max } }),
    totalSearchResults: 0,
    setTotalSearchResults: (total: number) => set({ totalSearchResults: total })
  }));

export const useGlobalStore: GlobalStore = internalStore;

if (typeof window !== 'undefined') {
  const w = window as unknown as { __logisnextGlobalStore?: unknown };
  if (!w.__logisnextGlobalStore) {
    w.__logisnextGlobalStore = internalStore;
  }
}
